import { Alert, Col, Row } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { Suspense, lazy, useEffect } from 'react'
import { IoHomeOutline } from 'react-icons/io5'

import { ActivityTiles } from '@/Ship/Components/Dashboard/ActivityTiles'
import { DashboardHeader } from '@/Ship/Components/Dashboard/DashboardHeader'
import { KpiStrip } from '@/Ship/Components/Dashboard/Overview/KpiStrip'
import { PipelineCard } from '@/Ship/Components/Dashboard/Overview/PipelineCard'
import { LeaderboardCard, RoutesCard, SourcesCard } from '@/Ship/Components/Dashboard/Overview/RankingCards'
import { ScheduleCard } from '@/Ship/Components/Dashboard/Overview/ScheduleCard'
import { Panel } from '@/Ship/Components/Dashboard/Overview/shared'
import {
    TransportApplicationsHint,
    TransportApplicationsTiles,
} from '@/Ship/Components/Dashboard/TransportApplicationsTiles'
import { TransportLoadingsTiles } from '@/Ship/Components/Dashboard/TransportLoadingsTiles'
import { TransportNoOffersTiles } from '@/Ship/Components/Dashboard/TransportNoOffersTiles'
import { EntityLayout } from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'
import { dashboardManager } from '@/Ship/Stores/DashboardManager'

import { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

// Графіки — окремими шматками збірки: `recharts` важкий, а головну відкривають усі
const TransportDynamicsCharts = lazy(() => import('@/Ship/Components/Dashboard/TransportDynamicsCharts'))
const TrendChart = lazy(() => import('@/Ship/Components/Dashboard/Overview/TrendChart'))
const ClientsDonut = lazy(() => import('@/Ship/Components/Dashboard/Overview/ClientsDonut'))

export const DASHBOARD_TITLE = 'Робочий стіл'

/** Заглушка блоку графіка, поки вантажиться його шматок збірки, — та сама геометрія. */
const ChartFallback = ({ title }: { title: string }) => <Panel title={title} loading skeletonRows={8} />

/**
 * Головна сторінка — «Робочий стіл» (`SPEC-015`, `SPEC-027` §3.5) у вигляді
 * огляду для керівника й логіста. Порядок — від того, що читають першим:
 *
 *  1. шапка: дата, «чиї цифри» (уся компанія чи співробітник), період
 *     рейтингів і фільтр по співробітнику (лише з `dashboard_others`);
 *  2. шість показників місяця до сьогодні проти того самого відрізка
 *     минулого (`/services/reports/overview`);
 *  3. «Оборот і прибуток» за 12 місяців і «Заявки в роботі» за статусами;
 *  4. операційний день: «Справи», «Заявки сьогодні», «Без пропозицій»;
 *  5. «Рейси на тиждень» і «Рейтинг логістів» (лише з `logisticians_all`;
 *     без нього на цьому місці — «Прибуток за клієнтами»);
 *  6. клієнти, маршрути, запити й тендери → заявки — за періодом;
 *  7. «Завантаження й розвантаження» й «Динаміка» перевезень (`SPEC-027`).
 *
 * Блок, якого бек не віддав (`null` без права глядача), не малюється.
 *
 * ⚠️ Живе в `Ship`, а не в контейнері: ключ `Index` і маршрут `/` ядро дає
 * лише Ship-сторінці `IndexPage`. Дані — готовими звітами по REST, три запити
 * на сторінку: жодного імпорту з `Containers` тут немає й бути не повинно.
 *
 * Кольори — лише токени теми: бренд (`colorPrimary`) — акцент каркаса й
 * позначки «чиї цифри», серії графіків — пресети antd без сірого. Числа
 * оновлюються без перезавантаження за подією `counters` (`DashboardManager`).
 */
const IndexPage: React.FC = observer(() => {
    useEffect(() => {
        // Ядровий `SetDocumentTitle` ставить заголовок лише ресурсам
        document.title = DASHBOARD_TITLE

        return dashboardManager.start()
    }, [])

    const hasLeaderboard = dashboardManager.overview
        ? dashboardManager.overview.leaderboard !== null
        : dashboardManager.canFilterByEmployee
    const operational = dashboardManager.showsNoOffers ? 8 : 12

    return (
        <div style={{ padding: '20px 24px 32px' }}>
            <EntityLayout>
                <DashboardHeader title={DASHBOARD_TITLE} />

                {dashboardManager.overviewError !== null ? (
                    <Alert
                        type="error"
                        showIcon
                        title={dashboardManager.overviewError}
                        data-testid="dashboard-overview-error"
                    />
                ) : null}

                <KpiStrip />

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={16}>
                        <Suspense fallback={<ChartFallback title="Оборот і прибуток" />}>
                            <TrendChart />
                        </Suspense>
                    </Col>
                    <Col xs={24} xl={8}>
                        <PipelineCard />
                    </Col>
                </Row>

                <Hint id="dashboard.desktop.tiles">
                    Плитки рахують незавершені справи, де відповідальний — ви або обраний у фільтрі співробітник;
                    адміністратор без фільтра бачить справи всієї компанії. Справа з терміном сьогодні входить і в «на
                    сьогодні», і в «на поточний тиждень», а прострочена — ще й у протерміновані.
                </Hint>
                <TransportApplicationsHint />

                {dashboardManager.error !== null ? (
                    <Alert type="error" showIcon title={dashboardManager.error} data-testid="dashboard-error" />
                ) : null}
                {dashboardManager.transportError !== null ? (
                    <Alert
                        type="error"
                        showIcon
                        title={dashboardManager.transportError}
                        data-testid="dashboard-transport-error"
                    />
                ) : null}

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12} xl={operational}>
                        <ActivityTiles />
                    </Col>
                    <Col xs={24} lg={12} xl={operational}>
                        <TransportApplicationsTiles />
                    </Col>
                    {dashboardManager.showsNoOffers ? (
                        <Col xs={24} lg={24} xl={8}>
                            <TransportNoOffersTiles />
                        </Col>
                    ) : null}
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={hasLeaderboard ? 14 : 12}>
                        <ScheduleCard />
                    </Col>
                    <Col xs={24} xl={hasLeaderboard ? 10 : 12}>
                        {hasLeaderboard ? (
                            <LeaderboardCard />
                        ) : (
                            <Suspense fallback={<ChartFallback title="Прибуток за клієнтами" />}>
                                <ClientsDonut />
                            </Suspense>
                        )}
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    {hasLeaderboard ? (
                        <Col xs={24} xl={9}>
                            <Suspense fallback={<ChartFallback title="Прибуток за клієнтами" />}>
                                <ClientsDonut />
                            </Suspense>
                        </Col>
                    ) : null}
                    <Col xs={24} lg={12} xl={hasLeaderboard ? 7 : 12}>
                        <RoutesCard />
                    </Col>
                    <Col xs={24} lg={12} xl={hasLeaderboard ? 8 : 12}>
                        <SourcesCard />
                    </Col>
                </Row>

                <TransportLoadingsTiles />

                {dashboardManager.showsDynamics ? (
                    <Suspense fallback={null}>
                        <TransportDynamicsCharts />
                    </Suspense>
                ) : null}
            </EntityLayout>
        </div>
    )
})

export default IndexPage

export const pageConfig: IPageConfig = {
    title: DASHBOARD_TITLE,
    icon: <IoHomeOutline />,
}
